import { pitElapsedSeconds } from './lapTime';

/**
 * Per-team record of how long each pit stop took, keyed by the pit count Apex
 * reports for that stop.
 *
 * Keying by pit count rather than appending to a list is what makes this safe to
 * run repeatedly over the same frames: re-observing a stop overwrites its entry
 * instead of adding a duplicate. That matters twice over — StrictMode invokes
 * render work twice in development, and seeking backwards in a replay rebuilds
 * team state from the first entry, replaying every stop again.
 */
export type PitStopStore = Map<string, Map<number, number>>;

/** The minimum a team needs to expose for its stops to be tracked. */
export interface PitObservable {
  pitCount: string;
  inBox: boolean;
  onTrackTime: string;
}

export interface TeamPitHistory {
  /** Stationary seconds per completed stop, in the order they happened. */
  completed: number[];
  /** Seconds stationary right now, or null when the kart is on track. */
  currentStationarySeconds: number | null;
}

export function emptyPitStopStore(): PitStopStore {
  return new Map();
}

function pitCountOf(team: PitObservable): number {
  const n = parseInt(team.pitCount, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Fold the current frame into the store. Only a boxed kart tells us anything:
 * Apex runs its stopwatch in the on-track cell while stationary, and we keep the
 * highest reading seen for the stop, which is its duration once the kart leaves.
 */
export function observePitStops(
  store: PitStopStore,
  teams: Map<string, PitObservable>,
): void {
  teams.forEach((team, teamId) => {
    if (!team.inBox) return;

    const elapsed = pitElapsedSeconds(team.onTrackTime);
    if (elapsed === null) return;

    let byStop = store.get(teamId);
    if (!byStop) {
      byStop = new Map();
      store.set(teamId, byStop);
    }

    const stop = pitCountOf(team);
    const seen = byStop.get(stop);
    // The stopwatch only ever climbs within a stop; taking the max also means a
    // replayed frame from earlier in the stop cannot shorten a recorded duration.
    if (seen === undefined || elapsed > seen) byStop.set(stop, elapsed);
  });
}

/**
 * Read the store back against the current frame.
 *
 * Stops numbered above the team's present pit count are ignored rather than
 * deleted: after a backwards seek they are in the future again, and they will be
 * re-observed identically on the way forward.
 */
export function derivePitHistory(
  store: PitStopStore,
  teams: Map<string, PitObservable>,
): Map<string, TeamPitHistory> {
  const result = new Map<string, TeamPitHistory>();

  teams.forEach((team, teamId) => {
    const byStop = store.get(teamId);
    const current = pitCountOf(team);

    if (!byStop) {
      result.set(teamId, { completed: [], currentStationarySeconds: null });
      return;
    }

    const completed: number[] = [];
    let currentStationarySeconds: number | null = null;

    for (const stop of Array.from(byStop.keys()).sort((a, b) => a - b)) {
      if (stop > current) continue;
      const seconds = byStop.get(stop)!;
      // The stop matching the live pit count is still running while boxed.
      if (stop === current && team.inBox) {
        currentStationarySeconds = seconds;
      } else {
        completed.push(seconds);
      }
    }

    result.set(teamId, { completed, currentStationarySeconds });
  });

  return result;
}

/** Median of a sample, or null when empty. Even counts average the middle pair. */
export function medianSeconds(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Typical stationary time across every completed stop on the board — the field's
 * pit loss, used to project a rival's remaining stops when we have no history of
 * our own to go on.
 */
export function fieldMedianPitLoss(
  history: Map<string, TeamPitHistory>,
): number | null {
  const all: number[] = [];
  history.forEach((h) => all.push(...h.completed));
  return medianSeconds(all);
}
